import React, { useState, useEffect, useMemo } from 'react';
import './TimeWindowEditView.css';
import { getAuthHeaders } from '../api/auth';
import { buildApiUrl } from '../lib/api';

interface TimeWindow {
    _id: string;
    day: string;
    timeSlot: string;
    timeWindowFrom: string;
    timeWindowTo: string;
    capacity: number;
    usedCapacity: number;
    availableCapacity: number;
    isActive: boolean;
}

interface TimeWindowEditViewProps {
    batchTransportId: string;
    currentTimeWindowId: string;
    calWeek: string;
    plant: string;
    fractionBez: string;
    onSave: (newTimeWindowId: string, timeWindowData: {
        timeWindowFrom: string;
        timeWindowTo: string;
        deliverydate: string;
    }) => Promise<void>;
    onClose: () => void;
    onSaveReady?: (saveHandler: () => Promise<void>, canSave: boolean) => void;
}

export const TimeWindowEditView: React.FC<TimeWindowEditViewProps> = ({
    batchTransportId,
    currentTimeWindowId,
    calWeek,
    plant,
    fractionBez,
    onSave,
    onClose,
    onSaveReady
}) => {
    const [timeWindows, setTimeWindows] = useState<TimeWindow[]>([]);
    const [selectedTimeWindowId, setSelectedTimeWindowId] = useState(currentTimeWindowId);
    const [selectedDay, setSelectedDay] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchTimeWindows = async () => {
            setLoading(true);
            setError(null);

            try {
                const year = new Date().getFullYear();
                const url = buildApiUrl('/api/planning/overview', {
                    calendarWeek: calWeek,
                    year: String(year),
                    plant: plant,
                    _t: String(Date.now())
                });

                const response = await fetch(url, {
                    headers: getAuthHeaders(),
                    credentials: 'include',
                });

                if (!response.ok) {
                    throw new Error(`Fehler beim Laden der Zeitfenster: ${response.status}`);
                }

                const data = await response.json() as {
                    overview: Record<string, Record<string, {
                        id: string;
                        isActive: boolean;
                        capacities: Record<string, { total: number; used: number; available: number }>;
                    }>>;
                };

                const extractedTimeWindows: TimeWindow[] = [];
                const days = ['MO', 'DI', 'MI', 'DO', 'FR', 'SA'];

                for (const day of days) {
                    const dayData = data.overview[day];
                    if (!dayData) continue;

                    for (const [timeSlot, slotData] of Object.entries(dayData)) {
                        const fractionCapacity = slotData.capacities[fractionBez];
                        if (!fractionCapacity) continue;

                        const { timeWindowFrom, timeWindowTo } = calculateTimeWindow(day, timeSlot, calWeek, year);

                        // Generiere eindeutige ID aus day+timeSlot+fraction, da slotData.id null ist
                        const timeWindowId = `${day}-${timeSlot}-${fractionBez}`;

                        extractedTimeWindows.push({
                            _id: timeWindowId,
                            day,
                            timeSlot,
                            timeWindowFrom,
                            timeWindowTo,
                            capacity: fractionCapacity.total,
                            usedCapacity: fractionCapacity.used,
                            availableCapacity: fractionCapacity.available,
                            isActive: slotData.isActive
                        });
                    }
                }

                extractedTimeWindows.sort((a, b) => {
                    const dayDiff = days.indexOf(a.day) - days.indexOf(b.day);
                    if (dayDiff !== 0) return dayDiff;

                    const aStartHour = parseInt((a.timeSlot || '').split('-')[0]) || 0;
                    const bStartHour = parseInt((b.timeSlot || '').split('-')[0]) || 0;
                    return aStartHour - bStartHour;
                });

                setTimeWindows(extractedTimeWindows);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Unbekannter Fehler');
            } finally {
                setLoading(false);
            }
        };

        void fetchTimeWindows();
    }, [calWeek, plant, fractionBez]);

    // Setze initialen Tag basierend auf dem aktuellen Zeitfenster
    useEffect(() => {
        if (timeWindows.length > 0 && !selectedDay) {
            const currentWindow = timeWindows.find(tw => tw._id === currentTimeWindowId);
            if (currentWindow) {
                setSelectedDay(currentWindow.day);
            } else {
                const dayWithCapacity = timeWindows.find(tw => tw.availableCapacity > 0);
                if (dayWithCapacity) {
                    setSelectedDay(dayWithCapacity.day);
                }
            }
        }
    }, [timeWindows, currentTimeWindowId, selectedDay]);

    // Expose save handler to parent
    useEffect(() => {
        if (onSaveReady) {
            const canSave = selectedTimeWindowId !== currentTimeWindowId && !saving && !loading;
            onSaveReady(handleSave, canSave);
        }
    }, [onSaveReady, selectedTimeWindowId, currentTimeWindowId, saving, loading]);

    const calculateTimeWindow = (day: string, timeSlot: string, calWeek: string, year: number) => {
        const dayMap: Record<string, number> = { 'MO': 0, 'DI': 1, 'MI': 2, 'DO': 3, 'FR': 4, 'SA': 5 };
        const dayOffset = dayMap[day];

        // ISO 8601: Erste Woche des Jahres ist die Woche mit dem ersten Donnerstag
        const jan1 = new Date(year, 0, 1);
        const jan1Day = jan1.getDay() || 7; // Sonntag = 7 statt 0

        // Tage bis zum ersten Donnerstag
        const daysToFirstThursday = (4 - jan1Day + 7) % 7;
        const firstThursday = new Date(year, 0, 1 + daysToFirstThursday);

        // Montag der ersten ISO-Woche
        const firstMonday = new Date(firstThursday);
        firstMonday.setDate(firstThursday.getDate() - 3); // Donnerstag - 3 = Montag

        // Berechne Montag der Zielwoche
        const weekNumber = parseInt(calWeek);
        const targetDate = new Date(firstMonday);
        targetDate.setDate(firstMonday.getDate() + (weekNumber - 1) * 7);

        // Addiere Wochentag-Offset
        targetDate.setDate(targetDate.getDate() + dayOffset);

        // Parse Zeitfenster
        const [startHour, endHour] = timeSlot.split('-').map(h => parseInt(h));

        targetDate.setHours(startHour, 0, 0, 0);
        const timeWindowFrom = targetDate.toISOString();

        const endDate = new Date(targetDate);
        endDate.setHours(endHour || startHour + 2, 0, 0, 0);
        const timeWindowTo = endDate.toISOString();

        return {
            timeWindowFrom,
            timeWindowTo
        };
    };

    // Gruppiere Zeitfenster nach Wochentag
    const timeWindowsByDay = useMemo(() => {
        const days = ['MO', 'DI', 'MI', 'DO', 'FR', 'SA'];
        const grouped: Record<string, TimeWindow[]> = {};

        days.forEach(day => {
            grouped[day] = timeWindows.filter(tw => tw.day === day);
        });

        return grouped;
    }, [timeWindows]);

    // Gefilterte Zeitfenster basierend auf ausgewähltem Tag
    const displayedTimeWindows = useMemo(() => {
        if (!selectedDay) return [];

        const windows = timeWindowsByDay[selectedDay] || [];
        const now = new Date();
        const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const currentHour = now.getHours();
        const currentMinute = now.getMinutes();

        return windows.map(tw => {
            const isCurrent = tw._id === currentTimeWindowId;

            // Prüfe, ob das Zeitfenster am heutigen Tag liegt
            const timeWindowDate = new Date(tw.timeWindowFrom);
            const isToday =
                timeWindowDate.getFullYear() === currentDate.getFullYear() &&
                timeWindowDate.getMonth() === currentDate.getMonth() &&
                timeWindowDate.getDate() === currentDate.getDate();

            // Wenn es heute ist, prüfe ob das Zeitfenster bereits begonnen hat
            let isPast = false;
            if (isToday && !isCurrent) {
                const timeWindowHour = timeWindowDate.getHours();
                const timeWindowMinute = timeWindowDate.getMinutes();

                // Zeitfenster ist vorbei, wenn die Startzeit bereits überschritten wurde
                isPast = (timeWindowHour < currentHour) ||
                    (timeWindowHour === currentHour && timeWindowMinute <= currentMinute);
            }

            return {
                ...tw,
                availableCapacity: isPast ? 0 : tw.availableCapacity
            };
        });
    }, [selectedDay, timeWindowsByDay, currentTimeWindowId]);

    // Zähle verfügbare Kapazität pro Tag
    const capacityByDay = useMemo(() => {
        const days = ['MO', 'DI', 'MI', 'DO', 'FR', 'SA'];
        const capacity: Record<string, number> = {};

        days.forEach(day => {
            const dayWindows = timeWindowsByDay[day] || [];
            capacity[day] = dayWindows.reduce((sum, tw) => sum + tw.availableCapacity, 0);
        });

        return capacity;
    }, [timeWindowsByDay]);

    // Extrahiere Datum pro Tag
    const dateByDay = useMemo(() => {
        const days = ['MO', 'DI', 'MI', 'DO', 'FR', 'SA'];
        const dates: Record<string, string> = {};

        days.forEach(day => {
            const dayWindows = timeWindowsByDay[day] || [];
            if (dayWindows.length > 0) {
                const date = new Date(dayWindows[0].timeWindowFrom);
                dates[day] = `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}.`;
            }
        });

        return dates;
    }, [timeWindowsByDay]);

    const handleSave = async () => {
        if (selectedTimeWindowId === currentTimeWindowId) {
            onClose();
            return;
        }

        const selectedTimeWindow = timeWindows.find(tw => tw._id === selectedTimeWindowId);
        if (!selectedTimeWindow) {
            setError('Bitte wählen Sie ein Zeitfenster aus');
            return;
        }

        setSaving(true);
        setError(null);

        try {
            await onSave(selectedTimeWindowId, {
                timeWindowFrom: selectedTimeWindow.timeWindowFrom,
                timeWindowTo: selectedTimeWindow.timeWindowTo,
                deliverydate: selectedTimeWindow.timeWindowFrom.split('T')[0]
            });
            onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Fehler beim Speichern');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="time-window-edit-loading">
                <span className="time-window-edit-spinner" />
                <p>Lade Zeitfenster...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="time-window-edit-error">
                <p>{error}</p>
                <button onClick={() => setError(null)}>OK</button>
            </div>
        );
    }

    if (timeWindows.length === 0) {
        return (
            <div className="time-window-edit-empty">
                <p>Keine Zeitfenster verfügbar für {fractionBez} in KW {calWeek}</p>
            </div>
        );
    }

    return (
        <div className="time-window-edit-view">
            {/* Tag-Auswahl Buttons */}
            <div className="day-selector">
                {['MO', 'DI', 'MI', 'DO', 'FR', 'SA'].map(day => {
                    const dayCapacity = capacityByDay[day] || 0;
                    const hasCapacity = dayCapacity > 0;
                    const hasCurrentWindow = timeWindowsByDay[day]?.some(tw => tw._id === currentTimeWindowId);

                    return (
                        <button
                            key={day}
                            type="button"
                            className={`day-button ${selectedDay === day ? 'day-button--active' : ''} ${!hasCapacity && !hasCurrentWindow ? 'day-button--disabled' : ''}`}
                            onClick={() => setSelectedDay(day)}
                            disabled={!hasCapacity && !hasCurrentWindow}
                        >
                            <div className="day-button-label">{day}</div>
                            {dateByDay[day] && (
                                <div className="day-button-date">{dateByDay[day]}</div>
                            )}
                            <div className="day-button-capacity">
                                {hasCapacity || hasCurrentWindow ? `${dayCapacity} frei` : 'Voll'}
                            </div>
                        </button>
                    );
                })}
            </div>

            {/* Zeitfenster für ausgewählten Tag */}
            {selectedDay && (
                <div className="time-windows-list">
                    <h3 className="time-windows-title">{selectedDay} - Zeitfenster</h3>

                    {displayedTimeWindows.length === 0 ? (
                        <div className="time-windows-empty">
                            <p>Keine Zeitfenster verfügbar für {selectedDay}</p>
                        </div>
                    ) : (
                        <div className="time-windows-grid">
                            {displayedTimeWindows.map((tw) => {
                                const isCurrent = tw._id === currentTimeWindowId;
                                const isSelected = tw._id === selectedTimeWindowId;
                                const usedPercentage = (tw.usedCapacity / tw.capacity) * 100;

                                return (
                                    <div
                                        key={tw._id}
                                        className={`time-window-card ${isSelected ? 'time-window-card--selected' : ''} ${isCurrent ? 'time-window-card--current' : ''} ${tw.availableCapacity === 0 && !isCurrent ? 'time-window-card--disabled' : ''}`}
                                        onClick={() => (tw.availableCapacity > 0 || isCurrent) && setSelectedTimeWindowId(tw._id)}
                                    >
                                        <div className="time-window-header">
                                            <div className="time-window-time">
                                                {tw.timeSlot} Uhr
                                            </div>
                                            <div className="time-window-capacity">
                                                <span className="time-window-capacity-value">{tw.availableCapacity} / {tw.capacity}</span>
                                                <span className="time-window-capacity-label">frei</span>
                                            </div>
                                        </div>

                                        {/* Kapazitätsbalken */}
                                        <div className="time-window-capacity-bar">
                                            <div
                                                className="time-window-capacity-bar-fill"
                                                style={{
                                                    width: `${usedPercentage}%`,
                                                    backgroundColor: usedPercentage >= 90 ? '#dc2626' : usedPercentage >= 70 ? '#f59e0b' : '#16a34a'
                                                }}
                                            />
                                        </div>

                                        {isCurrent && (
                                            <div className="time-window-current-badge">
                                                Aktuell
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
